import React, { useState, useEffect, useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Slider, Typography, IconButton, Button } from "@material-ui/core";
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import {Earth} from "./Earth"
import {Satellite} from "./Satellite"
import {calculateSatelliteCount} from "./Satellite"
import {generateSkewedNumber} from "./Satellite"

const START_YEAR = 1960;
const END_YEAR = 2026;
const ANIMATION_MS_PER_YEAR = 300;

const createStableSatellitePool = (count) => (
  new Array(count).fill().map(() => ({
    radius: generateSkewedNumber(205, 0.5, 7),
    theta: Math.random() * 2 * Math.PI,
    phi: Math.acos(2 * Math.random() - 1),
    offset: Math.random() * 2 * Math.PI,
    direction: Math.random() > 0.5 ? 1 : -1,
  }))
);

const getInterpolatedSatelliteCount = (year) => {
  const lowerYear = Math.floor(year);
  const upperYear = Math.ceil(year);

  if (lowerYear === upperYear) {
    return calculateSatelliteCount(lowerYear);
  }

  const lowerCount = calculateSatelliteCount(lowerYear);
  const upperCount = calculateSatelliteCount(upperYear);
  const progress = year - lowerYear;

  return Math.round(lowerCount + (upperCount - lowerCount) * progress);
};

export default function Home(props) {
    const [displayYear, setDisplayYear] = useState(START_YEAR);
    const [isPlaying, setIsPlaying] = useState(true);
    const finalYear = Math.min(new Date().getFullYear(), END_YEAR);
    const animationFrameRef = useRef(null);
    const previousFrameTimeRef = useRef(null);
    const stableSatellites = useMemo(
      () => createStableSatellitePool(calculateSatelliteCount(END_YEAR)),
      []
    );
    const displayYearLabel = Math.floor(displayYear);
    const satelliteCount = getInterpolatedSatelliteCount(displayYear);
    const satellites = useMemo(
      () => stableSatellites.slice(0, satelliteCount),
      [satelliteCount, stableSatellites]
    );

    useEffect(() => {
      if (!isPlaying) {
        previousFrameTimeRef.current = null;
        return undefined;
      }

      let shouldContinue = true;

      const animateYear = (timestamp) => {
        if (previousFrameTimeRef.current === null) {
          previousFrameTimeRef.current = timestamp;
        }

        const deltaMs = timestamp - previousFrameTimeRef.current;
        previousFrameTimeRef.current = timestamp;

        setDisplayYear((previousYear) => {
          const nextYear = Math.min(
            finalYear,
            previousYear + deltaMs / ANIMATION_MS_PER_YEAR
          );

          if (nextYear >= finalYear) {
            shouldContinue = false;
          }

          return nextYear;
        });

        if (shouldContinue) {
          animationFrameRef.current = window.requestAnimationFrame(animateYear);
        } else {
          setIsPlaying(false);
          previousFrameTimeRef.current = null;
        }
      };

      animationFrameRef.current = window.requestAnimationFrame(animateYear);

      return () => {
        shouldContinue = false;
        if (animationFrameRef.current) {
          window.cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, [finalYear, isPlaying]);
    
    const handleSliderChange = (event, newValue) => {
      previousFrameTimeRef.current = null;
      setDisplayYear(newValue);
    };
  
    const togglePlayState = () => {
      if (displayYear >= finalYear) {
        setDisplayYear(START_YEAR);
      }
      setIsPlaying((prevIsPlaying) => !prevIsPlaying);
    };
  
    return (
      <div style={{ position: "relative", height: "100vh", width: "100vw" }}>
              <Canvas style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", background: "black" }}>
              <PerspectiveCamera position={[520, 0, 0]} makeDefault />
              <ambientLight />
              <pointLight position={[10, 10, 10]} />
              <Earth />
              {satellites.map((satellite, i) => (
                <Satellite key={i} {...satellite} />
              ))}
              <OrbitControls enableZoom={false} />

              <EffectComposer>
                <Bloom 
                  luminanceThreshold={0.1} 
                  luminanceSmoothing={0.075} 
                  intensity={0.4}
                />
              </EffectComposer>
            </Canvas>

            <div style={{ position: "absolute", top: "85%", width: "100%", textAlign: "center" }}>
        <IconButton onClick={togglePlayState} style={{ color: "white" }}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
      </div>
    
            <div style={{ position: "absolute", top: "90%", width: "100%", textAlign: "center" }}>
              <Slider 
                value={displayYear} 
                min={START_YEAR} 
                max={finalYear} 
                step={1} 
                onChange={handleSliderChange}
                style={{ color: "white", width: "33%" }}
              />
            </div>
    
            <div style={{ position: "absolute", top: "70%", width: "100%", textAlign: "center" }}>
              <Typography variant="h2" style={{ color: "white" }}>
                {displayYearLabel}
              </Typography>
              <Typography variant="h5" style={{color: "white"}}>
                {satelliteCount} Spacecraft
              </Typography>
            </div>
            
            <div
        style={{
        }}
      ><Button
      variant="contained"
      color="primary"
      style={{
        background: "transparent",
        border: "none",
        boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
        backdropFilter: "blur(10px)",
        color: "white",
        padding: "10px 20px",
        borderRadius: "0px",
      }}
    >
      Home
    </Button>
    <div className="title-text">EO Unlocked</div>
    </div>
            <div
              style={{
                position: "absolute",
                top: "18px",
                right: "18px",
                zIndex: 1,
                maxWidth: "360px",
                color: "rgba(255, 255, 255, 0.62)",
                fontSize: "11px",
                lineHeight: 1.35,
                textAlign: "right",
              }}
            >
              Source:{" "}
              <a
                href="https://www.ucs.org/resources/satellite-database"
                target="_blank"
                rel="noreferrer"
                style={{ color: "inherit" }}
              >
                Union of Concerned Scientists Satellite Database
              </a>
              ; 2026 endpoint extrapolated to ~1,500 EO satellites.
            </div>
            <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)"
        }}
      >
<Button
  variant="contained"
  color="primary"
  style={{
    background: "transparent",
    border: "none",
    boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    color: "white",
    padding: "20px 40px",
    borderRadius: "20px",
  }}
  onClick={props.onEnter}
>
  Enter
</Button>
      </div>
          </div>
        );
      }
    
